import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import {
  OrganizationModel,
  OrganizationFilter,
  SaveOrganizationPayload
} from '../../models/school/organization.model';
import { SchoolModel } from '../../models/school/school.model';
import { StringExtensionModel } from '../../models/extensions/string-extension.model';

@Injectable({
  providedIn: 'root'
})
export class AdminOrganizationService {

  constructor(private authSv: AuthService) {}

  getOrganizations(filters?: OrganizationFilter): Observable<OrganizationModel[]> {
    let url = '/admin/organizations';
    if (filters) {
      url += StringExtensionModel.queryString(filters);
    }
    return this.authSv.get(url).pipe(map(res => OrganizationModel.generateList(res.data)));
  }

  getOrganization(id: string): Observable<OrganizationModel> {
    return this.authSv.get(`/admin/organizations/${id}`).pipe(map(res => OrganizationModel.generate(res.data)));
  }

  saveOrganization(payload: SaveOrganizationPayload): Observable<OrganizationModel> {
    return this.authSv.patch('/admin/organizations', payload).pipe(map(res => OrganizationModel.generate(res.data)));
  }

  deleteOrganization(id: string): Observable<string> {
    return this.authSv.delete(`/admin/organizations/${id}`).pipe(map(res => res.data._id));
  }

  addSchoolToOrganization(organizationId: string, schoolId: string): Observable<{ organization: OrganizationModel, school: SchoolModel }> {
    return this.authSv.patch(`/admin/organizations/${organizationId}/addSchool/${schoolId}`, {}).pipe(
      map(res => ({
        organization: OrganizationModel.generate(res.data.organization),
        school: SchoolModel.generate(res.data.school)
      }))
    );
  }

  removeSchoolFromOrganization(organizationId: string, schoolId: string): Observable<{ organization: OrganizationModel, school: SchoolModel }> {
    return this.authSv.delete(`/admin/organizations/${organizationId}/removeSchool/${schoolId}`).pipe(
      map(res => ({
        organization: OrganizationModel.generate(res.data.organization),
        school: SchoolModel.generate(res.data.school)
      }))
    );
  }

  bulkRemoveSchoolsFromOrganization(schoolIds: string[], organizationId: string): Observable<{ removed: number, failed: number }> {

    if (!schoolIds.length) {
      return of({ removed: 0, failed: 0 });
    }

    const calls = schoolIds.map(id =>
      this.removeSchoolFromOrganization(organizationId, id).pipe(
        map(() => ({ failed: false })),
        catchError(() => of({ failed: true }))
      )
    );

    return forkJoin(calls).pipe(
      map(results => ({
        removed: results.filter(r => !r.failed).length,
        failed:  results.filter(r => r.failed).length
      }))
    );
  }

  bulkAddSchoolsToOrganization(schoolIds: string[], organizationId: string): Observable<{ added: number, skipped: number, failed: number }> {

    if (!schoolIds.length) {
      return of({ added: 0, skipped: 0, failed: 0 });
    }

    const calls = schoolIds.map(id =>
      this.addSchoolToOrganization(organizationId, id).pipe(
        map(() => ({ failed: false })),
        catchError(() => of({ failed: true }))
      )
    );

    return forkJoin(calls).pipe(
      map(results => ({
        added:   results.filter(r => !r.failed).length,
        skipped: 0,
        failed:  results.filter(r => r.failed).length
      }))
    );
  }
}
